import { useState, useCallback, DragEvent } from 'react';
import { useFileStore, FileEntry } from '@/stores/files.store';
import { useFileOperations } from './use-file-operations';
import { useLogStore } from '@/stores/log.store';
import { useDeviceStore } from '@/stores/device.store';
import { webAdbService } from '@/services/webadb.service';
import { fileSystemService } from '@/services/filesystem.service';

export interface DragState {
  isDragging: boolean;
  dragSource: 'left' | 'right' | null;
  draggedItems: FileEntry[];
}

export const useDragDrop = (side: 'left' | 'right') => {
  const [isDropTarget, setIsDropTarget] = useState(false);
  const [dropTargetFolder, setDropTargetFolder] = useState<string | null>(null);
  const { left, right } = useFileStore();
  const { transferSelected } = useFileOperations();
  const { addLog } = useLogStore();
  const { adb } = useDeviceStore();

  const sideState = side === 'left' ? left : right;

  const handleDragStart = useCallback((e: DragEvent, file: FileEntry) => {
    const selectedFiles = sideState.selectedPaths.includes(file.path)
      ? sideState.files.filter(f => sideState.selectedPaths.includes(f.path))
      : [file];

    e.dataTransfer.setData('application/json', JSON.stringify({
      source: side,
      paths: selectedFiles.map(f => f.path),
      files: selectedFiles
    }));
    e.dataTransfer.effectAllowed = 'copyMove';
  }, [side, sideState]);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDropTarget(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDropTarget(false);
  }, []);

  const handleDrop = useCallback(async (e: DragEvent) => {
    e.preventDefault();
    setIsDropTarget(false);

    try {
      const data = e.dataTransfer.getData('application/json');
      if (!data) return;

      const { source } = JSON.parse(data) as { source: 'left' | 'right'; paths: string[] };

      if (source === side) {
        addLog('info', '不能拖放到同一面板');
        return;
      }

      await transferSelected(source);
    } catch (error: any) {
      addLog('error', `拖放失败: ${error.message}`);
    }
  }, [side, transferSelected, addLog]);

  // 文件夹拖放处理
  const handleFolderDragOver = useCallback((e: DragEvent, folder: FileEntry) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setDropTargetFolder(folder.path);
  }, []);

  const handleFolderDragLeave = useCallback((e: DragEvent) => {
    e.stopPropagation();
    setDropTargetFolder(null);
  }, []);

  const handleFolderDrop = useCallback(async (e: DragEvent, targetFolder: FileEntry) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTargetFolder(null);

    try {
      const data = e.dataTransfer.getData('application/json');
      if (!data) return;

      const { source, paths } = JSON.parse(data) as {
        source: 'left' | 'right';
        paths: string[];
        files?: FileEntry[];
      };

      if (source === side) {
        addLog('info', '不能拖放到同一面板的文件夹');
        return;
      }

      const sourceState = source === 'left' ? left : right;
      const isSourceAdb = sourceState.path.startsWith('adb://');
      const isTargetAdb = sideState.path.startsWith('adb://');

      // 从源面板获取拖动的文件信息
      const draggedFiles = sourceState.files.filter(f => paths.includes(f.path));

      if (!draggedFiles || draggedFiles.length === 0) {
        addLog('error', '未找到要拖放的文件');
        return;
      }

      addLog('info', `正在复制到文件夹: ${targetFolder.name}`);

      // 根据源和目标类型执行不同的复制操作
      if (isSourceAdb && !isTargetAdb) {
        // ADB -> Local 文件夹
        if (!adb || !targetFolder.handle || targetFolder.handle.kind !== 'directory') return;

        const targetDirHandle = targetFolder.handle as FileSystemDirectoryHandle;
        for (const file of draggedFiles) {
          if (file.type === 'file') {
            await webAdbService.downloadFileToLocal(adb, file.path, targetDirHandle, file.name);
          } else {
            await webAdbService.downloadDirectoryToLocal(adb, file.path, targetDirHandle, file.name);
          }
          addLog('success', `已复制: ${file.name} -> ${targetFolder.name}/`);
        }
      } else if (!isSourceAdb && isTargetAdb) {
        // Local -> ADB 文件夹
        if (!adb) return;

        const targetPath = targetFolder.path;
        for (const file of draggedFiles) {
          if (!file.handle) continue;

          if (file.type === 'file') {
            const fileData = await (file.handle as FileSystemFileHandle).getFile();
            await webAdbService.uploadFile(adb, targetPath, fileData);
          } else {
            await webAdbService.uploadDirectory(adb, targetPath, file.handle as FileSystemDirectoryHandle);
          }
          addLog('success', `已复制: ${file.name} -> ${targetFolder.name}/`);
        }
      } else if (!isSourceAdb && !isTargetAdb) {
        // Local -> Local 文件夹
        if (!targetFolder.handle || targetFolder.handle.kind !== 'directory') return;

        const targetDirHandle = targetFolder.handle as FileSystemDirectoryHandle;
        for (const file of draggedFiles) {
          if (!file.handle) continue;
          await fileSystemService.copyEntry(file.handle, targetDirHandle);
          addLog('success', `已复制: ${file.name} -> ${targetFolder.name}/`);
        }
      } else {
        // ADB -> ADB 文件夹
        if (!adb) return;

        const targetPath = targetFolder.path;
        for (const file of draggedFiles) {
          const destPath = targetPath + '/' + file.name;
          await webAdbService.executeShell(adb, `cp -r "${file.path}" "${destPath}"`);
          addLog('success', `已复制: ${file.name} -> ${targetFolder.name}/`);
        }
      }

      addLog('success', '拖放复制完成');
    } catch (error: any) {
      addLog('error', `拖放到文件夹失败: ${error.message}`);
    }
  }, [side, sideState, left, right, adb, addLog]);

  return {
    isDropTarget,
    dropTargetFolder,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFolderDragOver,
    handleFolderDragLeave,
    handleFolderDrop
  };
};
